import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { COLORS, FONT } from './tokens';
import { MaterialSymbol } from './MaterialSymbol';

const browserSaysOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

/** `navigator.onLine` is often false while requests still succeed. Confirm with one. */
const confirmOffline = (): Promise<boolean> => {
  if (!browserSaysOffline()) return Promise.resolve(false);
  return fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' })
    .then(() => false)
    .catch(() => true);
};

export const OfflineStrip = (): ReactElement | null => {
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    if (typeof navigator === 'undefined') return undefined;
    let cancelled = false;
    const update = (): void => {
      void confirmOffline().then(isOffline => {
        if (!cancelled) setOffline(isOffline);
      });
    };
    const markOnline = (): void => setOffline(false);
    update();
    window.addEventListener('online', markOnline);
    window.addEventListener('offline', update);
    return () => {
      cancelled = true;
      window.removeEventListener('online', markOnline);
      window.removeEventListener('offline', update);
    };
  }, []);

  if (!offline) return null;

  return (
    <Box
      role="status"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 90,
        height: '36px',
        width: '100%',
        backgroundColor: COLORS.liveChipBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
      }}
    >
      <MaterialSymbol name="cloud_off" size={16} color={COLORS.liveChipText} />
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: '13px',
          color: COLORS.liveChipText,
        }}
      >
        You&apos;re offline
      </Typography>
    </Box>
  );
};
